import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Globe, Search, Headset, CalendarCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";

const ROLE = { user: "Cliente", assistant: "Assistente IA", human: "Profissional" };
const ROLE_CLS = { user: "text-[#D4AF37]", assistant: "text-white/50", human: "text-emerald-400" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const fieldCls = "bg-[#141414] border border-[#222] focus:border-[#D4AF37] outline-none text-xs text-white px-3 py-2";

const SituationBadge = ({ c }) => {
  if (c.handoff_active)
    return <span className="text-[10px] px-2 py-0.5 bg-amber-500/15 text-amber-400 border border-amber-500/40 inline-flex items-center gap-1"><Headset size={10} /> Precisa de humano</span>;
  if (c.prebook_count > 0)
    return <span className="text-[10px] px-2 py-0.5 bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 inline-flex items-center gap-1"><CalendarCheck size={10} /> Pré-agendou</span>;
  return null;
};

const ConversationDetail = ({ sessionId, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (sessionId) adminApi.get(`/conversations/${encodeURIComponent(sessionId)}`).then((r) => setData(r.data));
    else setData(null);
  }, [sessionId]);

  const s = data?.session || {};
  const appt = data?.appointments?.[0];
  return (
    <Dialog open={!!sessionId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="conversation-detail-dialog" className="bg-[#0A0A0A] border-[#222] text-white rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light">
            {s.contact_name || appt?.name || "Conversa"}{" "}
            <span className="text-xs text-white/40 font-body">#{sessionId?.slice(0, 12)}</span>
          </DialogTitle>
        </DialogHeader>
        {!data ? (
          <div className="text-white/50 text-sm py-6">Carregando...</div>
        ) : (
          <>
            <div data-testid="conversation-detail-meta" className="text-xs text-white/60 flex flex-wrap gap-x-4 gap-y-1">
              <span>Canal: {s.channel === "whatsapp" ? "WhatsApp" : "Site"}</span>
              <span>Telefone: {s.phone || appt?.phone || "—"}</span>
              {s.handoff_reason && <span className="text-amber-400">Atendimento humano: {s.handoff_reason}</span>}
              {data.appointments.map((a) => (
                <span key={a.id} className="text-emerald-400">Pré-agendamento: {a.service} — {a.preferred_date} {a.preferred_time} ({a.status})</span>
              ))}
            </div>
            <div data-testid="conversation-detail-messages" className="max-h-[60vh] overflow-y-auto space-y-3 bg-[#050505] border border-[#1a1a1a] p-4">
              {data.messages.map((m, i) => (
                <div key={i} className="text-sm">
                  <div className={`text-[10px] uppercase tracking-widest ${ROLE_CLS[m.role] || "text-white/50"}`}>
                    {ROLE[m.role] || m.role} · {fmt(m.created_at)}
                  </div>
                  <div className="text-white/85 whitespace-pre-wrap">{m.content}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ConversationsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", start: "", end: "", status: "" });
  const [open, setOpen] = useState(null);

  const load = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const r = await adminApi.get("/conversations", { params: { ...params, limit: 200 } });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(filters), 350);
    return () => clearTimeout(t);
  }, [filters, load]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section data-testid="admin-conversations" className="bg-[#0A0A0A] border border-[#222] mb-12">
      <div className="p-6 border-b border-[#222] flex flex-wrap items-center gap-3 justify-between">
        <h2 className="font-heading text-2xl font-light">Conversas</h2>
        <span data-testid="conversations-count" className="text-xs text-white/50">{items.length} conversas</span>
      </div>
      <div className="px-6 py-4 border-b border-[#222] flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-2.5 text-white/40" />
          <input data-testid="conversations-search" placeholder="Nome ou telefone" value={filters.q} onChange={set("q")} className={`${fieldCls} pl-8 w-56`} />
        </div>
        <label className="text-xs text-white/50">De</label>
        <input data-testid="conversations-start" type="date" value={filters.start} onChange={set("start")} className={fieldCls} />
        <label className="text-xs text-white/50">Até</label>
        <input data-testid="conversations-end" type="date" value={filters.end} onChange={set("end")} className={fieldCls} />
        <select data-testid="conversations-status" value={filters.status} onChange={set("status")} className={fieldCls}>
          <option value="">Todas as situações</option>
          <option value="humano">Precisa de atendimento humano</option>
          <option value="pre_agendamento">Com pré-agendamento</option>
          <option value="sem_agendamento">Sem agendamento</option>
        </select>
      </div>
      {loading ? (
        <div className="p-10 text-center text-white/50">Carregando...</div>
      ) : items.length === 0 ? (
        <div data-testid="conversations-empty" className="p-10 text-center text-white/50">Nenhuma conversa encontrada.</div>
      ) : (
        <div className="divide-y divide-[#222] max-h-[600px] overflow-y-auto">
          {items.map((c) => {
            const Icon = c.channel === "whatsapp" ? MessageCircle : Globe;
            return (
              <button
                key={c.session_id}
                data-testid={`conversation-row-${c.session_id}`}
                onClick={() => setOpen(c.session_id)}
                className="w-full text-left p-5 flex items-start gap-4 hover:bg-[#111] transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <Icon size={16} strokeWidth={1.5} className="text-[#D4AF37]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-white/90">{c.name || "Cliente não identificada"}</span>
                    {c.phone && <span className="text-white/50 text-xs">{c.phone}</span>}
                    <SituationBadge c={c} />
                  </div>
                  <div className="text-xs text-white/60 truncate mt-1">{c.last_message}</div>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-white/40">
                    <span>{fmt(c.last_at)}</span>
                    <span>•</span>
                    <span>{c.message_count} mensagens</span>
                    <span>•</span>
                    <span>{c.channel === "whatsapp" ? "WhatsApp" : "Site"}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <ConversationDetail sessionId={open} onClose={() => setOpen(null)} />
    </section>
  );
};
